import {SoundSlider} from '@gongxigames/framework/slot/modules/bottompanel/SoundSlider';
import {sound as Sound} from '@pixi/sound';
import {InteractionEvent, Point, Sprite} from 'pixi.js';
import {
    Button,
    Checkbox,
    CommandLineInterface,
    CoreDI, CurrencyFormatter, inject,
    LanguageService, ObjectBuilder, Text, DeviceType
} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {LockManager} from '@gongxigames/framework/core/fragments/locker/LockManager';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {View} from '@gongxigames/framework/slot/fragments/module/view';
import {KeyboardKeyCodes} from '@gongxigames/framework/core/types/IKeyboard';

export class BottomPanelView extends View<GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BottomPanelView');
    public static layer = LAYERS.KEYPAD;
    public static platform: DeviceType = DeviceType.DESKTOP;
    public static readonly SOUND_BUTTON_CLICKED: symbol = Symbol('SOUND_BUTTON_CLICKED');
    public static readonly HOME_CLICKED: string = 'HOME_CLICKED';
    public static readonly HISTORY_CLICKED: string = 'HISTORY_CLICKED';
    public static readonly SUPPORT_CLICKED: string = 'SUPPORT_CLICKED';
    public static readonly LOGOUT_CLICKED: string = 'LOGOUT_CLICKED';
    public static readonly GAME_RULES_CLICKED: string = 'GAME_RULES_CLICKED';
    public static readonly CASHIER_CLICKED: string = 'CASHIER_CLICKED';
    public static readonly FULLSCREEN_CLICKED: string = 'FULLSCREEN_CLICKED';
    public static readonly BALANCE_FIELD_UPDATED: string = 'BALANCE_FIELD_UPDATED';

    private static readonly BUTTONS_OFFSET: number = 55;

    protected _sprite!: Sprite;

    @inject(CoreDI.CurrencyFormatter)
    private _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.LanguageService)
    private _languageService!: LanguageService;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    private lockManager: LockManager = new LockManager();
    private _homeButton!: Button;
    private _historyButton!: Button;
    private _supportButton!: Button;
    private _logoutButton!: Button;
    private _fullscreenButton!: Button;
    private _cashierButton!: Button;
    private _gameRulesButton!: Button;
    private _balanceField!: Text;
    private _pointsBalance!: Text;
    private _soundCheckbox!: Checkbox;
    private _soundBar!: Sprite;
    private _soundSlider!: SoundSlider;
    private _volume = 0;
    private _isFullscreen = false;

    public init(): void {
        super.init();

        const sprite = this.objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.bottomPanel);

        this._sprite = sprite;
        this._homeButton = this.addButton('homeButton');
        this._supportButton = this.addButton('supportButton');
        this._logoutButton = this.addButton('logoutButton');
        this._historyButton = this.addButton('historyButton');
        this._gameRulesButton = this.addButton('gameRulesButton');
        this._fullscreenButton = this.addButton('fullscreenButton');
        this._cashierButton = this.addButton('cashierButton');
        this._balanceField = (sprite.getChildByName<Sprite>('balancePanel')).getChildByName<Text>('fieldText');
        this._pointsBalance = sprite.getChildByName<Text>('pointsBalance').getChildByName<Text>('fieldText');

        const soundPanel = sprite.getChildByName<Sprite>('soundPanel');
        const soundOnButton = soundPanel.getChildByName<Button>('soundOnButton');
        const soundOffButton = soundPanel.getChildByName<Button>('soundOffButton');

        this._soundBar = soundPanel.getChildByName<Sprite>('soundBar');
        this._soundSlider = new SoundSlider(this._soundBar);

        this._soundCheckbox = new Checkbox(soundOnButton, soundOffButton, this.onSoundCheckboxChanged.bind(this));
        this.lockManager.add('soundCheckbox', this._soundCheckbox.setDisabled.bind(this._soundCheckbox));

        /* develblock:start */
        this._cli.addCommand('disableSound', () => this._soundCheckbox.setValue(false, true));
        this._cli.addCommand('enableSound', () => this._soundCheckbox.setValue(true, true));
        /* develblock:end */

        this._soundBar.visible = false;

        this._soundSlider.changedCallback = this.onVolumeChanged.bind(this);

        soundOnButton.on('mouseover', this.soundButtonOver.bind(this));
        soundOffButton.on('mouseover', this.soundButtonOver.bind(this));
        soundOnButton.on('mouseout', this.soundButtonOut.bind(this));
        soundOffButton.on('mouseout', this.soundButtonOut.bind(this));

        this._soundBar.interactive = true;
        this._soundBar.buttonMode = true;
        this._soundBar.on('mouseout', this.onSoundBarOut.bind(this));
        this._soundBar.on('mouseupoutside', this.onSoundBarOut.bind(this));

        sprite.getChildByName('balancePanel').visible = this.getSettings<SlotSettings>().get('showBalance');

        const parameters = this.getSettings<SlotSettings>().getAllProperties();

        if (parameters.homeUrl && !parameters.hideHomeButton) {
            this._homeButton.callback = () => this.playSoundButtonClickAndEmitEvent(BottomPanelView.HOME_CLICKED);
        } else {
            this._homeButton.visible = false;
        }

        if (parameters.historyUrl && !parameters.hideHistoryButton) {
            this._historyButton.callback = () => this.playSoundButtonClickAndEmitEvent(BottomPanelView.HISTORY_CLICKED);
        } else {
            this._historyButton.visible = false;
        }

        if (parameters.supportUrl && !parameters.hideSupportButton) {
            this._supportButton.callback = () => this.playSoundButtonClickAndEmitEvent(BottomPanelView.SUPPORT_CLICKED);
        } else {
            this._supportButton.visible = false;
        }

        if (parameters.logoutUrl && !parameters.hideLogoutButton) {
            this._logoutButton.callback = () => this.playSoundButtonClickAndEmitEvent(BottomPanelView.LOGOUT_CLICKED);
        } else {
            this._logoutButton.visible = false;
        }

        if (parameters.hideGameRulesButton) {
            this._gameRulesButton.visible = false;
        } else {
            this._gameRulesButton.callback = () => this.playSoundButtonClickAndEmitEvent(BottomPanelView.GAME_RULES_CLICKED);
        }

        if (parameters.cashierUrl && !parameters.hideCashierButton) {
            this._cashierButton.callback = () => this.playSoundButtonClickAndEmitEvent(BottomPanelView.CASHIER_CLICKED);
        } else {
            this._cashierButton.visible = false;
        }

        if (!parameters.fullscreenDisabled && this._device.fullscreen) {
            this._fullscreenButton.callback = this.onFullscreenButtonClicked.bind(this);
        } else {
            this._fullscreenButton.visible = false;
        }

        if (parameters.soundEnabled) {
            /* develblock:start */
            this.eventManager.on('General.UI.KEY_DOWN', this.toggleSoundsMute.bind(this), this);
            /* develblock:end */
            this.volume = parameters.soundVolume;
            this._soundSlider.setValue(this.volume, false);
        } else {
            soundPanel.visible = false;
        }

        soundPanel.visible = !this.getSettings<SlotSettings>().get('hideSoundCheckBox');

        if (parameters.soundsMuted) {
            this._soundSlider.setValue(0);
            Sound.muteAll();
            this._soundCheckbox.setValue(false, false);
        } else {
            this._soundCheckbox.setValue(true, false);
            this.toggleSoundSlider(true);
        }

        if (parameters.isPointsCurrencyEnabled) {
            this._pointsBalance.visible = true;
        } else {
            this._pointsBalance.visible = false;
        }

        this.container.addChild(sprite);
        this.updatePositions();
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.eventManager.on('General.Game.START_GAME', this.onStartGame.bind(this), this);
    }

    public disableAllButtons(reason: symbol): void {
        this.lockManager.lockAll(reason);
    }

    public enableAllButtons(reason: symbol): void {
        this.lockManager.unlockAll(reason);
    }

    public refreshBalance(balance: number): void {
        this._balanceField.text = this._languageService.get('cashLabel') + ':  ' + this._currencyFormatter.formatCents(balance, true);
        const bounds = this._balanceField.getBounds();

        this.emit(BottomPanelView.BALANCE_FIELD_UPDATED, bounds);
    }

    public refreshPointsBalance(points: number): void {
        this._pointsBalance.text = `${this._languageService.get('pointsLabel')}: ${this._currencyFormatter.formatCents(points)}`;
    }

    public setSoundCheckboxValue(isOn: boolean): void {
        if (this._soundCheckbox.value !== isOn) {
            this._soundCheckbox.setValue(isOn);

            if (isOn) {
                this._soundSlider.setValue(this.volume);
            } else {
                this._soundSlider.setValue(0);
            }
        }
    }

    public setSoundSliderValue(level: number): void {
        this._soundSlider.setValue(level);
    }

    set volume(value: number) {
        this._volume = value;
        Sound.volumeAll = value;
        this.getSettings<SlotSettings>().set('soundVolume', value);
    }

    get volume(): number {
        return this._volume;
    }

    /**
     * update positions of buttons depending on their visibility
     */
    protected updatePositions(): void {
        // first element's position + width + offset
        let x = this._sprite.children[0].x + this._sprite.children[0].getLocalBounds().width + BottomPanelView.BUTTONS_OFFSET;

        for (let i = 1; i < this._sprite.children.length; i++) {
            const child = this._sprite.children[i];

            if (child.visible) {
                child.x = x;
                x += child.getLocalBounds().width + BottomPanelView.BUTTONS_OFFSET;
            }
        }
    }

    protected addButton(name: string): Button {
        const button = this._sprite.getChildByName<Button>(name);

        this.lockManager.add(name, button.setDisabled.bind(button));

        return button;
    }

    protected onSoundCheckboxChanged(isOn: boolean): void {
        this.soundPlayer.play('buttontoggle');
        this.toggleSoundSlider(isOn);
    }

    protected onStartGame(): void {
        this.container.parentLayer = LAYERS.WATERMARK;
    }

    private toggleSoundSlider(isOn: boolean): void {
        const volume = isOn ? this.volume : 0;

        this.emit(BottomPanelView.SOUND_BUTTON_CLICKED, isOn);
        this._soundSlider.setValue(volume);
    }

    protected playSoundButtonClickAndEmitEvent(event: string): void {
        this.soundPlayer.play('buttonclick');
        this.emit(event);
    }

    protected onFullscreenButtonClicked(): void {
        this.soundPlayer.play('buttonclick');
        this._isFullscreen = !this._isFullscreen;
        this.emit(BottomPanelView.FULLSCREEN_CLICKED, this._isFullscreen);
    }

    protected soundButtonOver(): void {
        this._soundBar.visible = true;
    }

    protected onSoundBarOut(): void {
        if (!this._soundSlider.drugging) {
            this._soundBar.visible = false;
        }
    }

    protected soundButtonOut(e: InteractionEvent): void {
        const contains = this.containsPoint(this._soundBar, e.data.getLocalPosition(this._soundBar));

        if (!this._soundSlider.drugging && !contains) {
            this._soundBar.visible = false;
        }
    }

    // we cannot use Sprite:containsPoint() because it won't work if sprite isn't visible
    protected containsPoint(sprite: Sprite, point: Point): boolean {
        const bounds = sprite.getLocalBounds();

        return point.x > 0 && point.x < bounds.width && point.y > 0 && point.y < bounds.height;
    }

    protected onVolumeChanged(value: number): void {
        if (Math.abs(value - this.volume) <= 0.02) {
            return;
        }

        if (value <= 0.02) {
            this._soundSlider.setValue(0);
            this.volume = 0;

            if (this._soundCheckbox.value) {
                this._soundCheckbox.setValue(false, false);
                this.emit(BottomPanelView.SOUND_BUTTON_CLICKED, false);
            }
        } else {
            this.volume = value;

            if (!this._soundCheckbox.value) {
                this._soundCheckbox.setValue(true, false);
                this.emit(BottomPanelView.SOUND_BUTTON_CLICKED, true);
            }
        }
    }

    private toggleSoundsMute(keyCode: number): void {
        const isSKey = keyCode === KeyboardKeyCodes.V;

        if (!isSKey) {
            return;
        }

        const isOn = this.getSettings<SlotSettings>().get('soundsMuted');

        this.emit(BottomPanelView.SOUND_BUTTON_CLICKED, isOn);

        if (isOn) {
            this._soundSlider.setValue(this.volume);
            this._soundCheckbox.setValue(true, false);
        } else {
            this._soundSlider.setValue(0);
            this._soundCheckbox.setValue(false, false);
        }
    }
}
